import { useState, useEffect, useCallback } from 'react';
import AuthService from '../services/authService';
import { resolveUserId } from '../utils/resolveUserId';

/**
 * Returns numeric DPG userId, resolving from profile when missing on auth user.
 */
const useResolvedUserId = (user) => {
  const readStoredUserId = () => AuthService.getCurrentUser()?.userId ?? null;

  const [resolvedUserId, setResolvedUserId] = useState(
    () => user?.userId ?? readStoredUserId()
  );
  const userIdentity = user?.id ?? user?.email ?? null;

  const resolveFromSources = useCallback(async () => {
    const authUser = AuthService.getCurrentUser();
    const source = user?.userId != null ? user : authUser;

    if (!source) {
      setResolvedUserId(null);
      return;
    }

    if (source.userId != null) {
      setResolvedUserId(source.userId);
      return;
    }

    const userId = await resolveUserId(source);
    setResolvedUserId(userId);
  }, [user?.userId, userIdentity]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const authUser = AuthService.getCurrentUser();
      const source = user?.userId != null ? user : authUser;

      if (!source) {
        if (!cancelled) setResolvedUserId(null);
        return;
      }

      if (source.userId != null) {
        if (!cancelled) setResolvedUserId(source.userId);
        return;
      }

      const userId = await resolveUserId(source);
      if (!cancelled) {
        setResolvedUserId(userId);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [user?.userId, userIdentity]);

  useEffect(() => {
    const handleUserLogin = () => {
      const storedUserId = readStoredUserId();
      if (storedUserId != null) {
        setResolvedUserId(storedUserId);
        return;
      }
      resolveFromSources();
    };

    window.addEventListener('userLogin', handleUserLogin);
    return () => window.removeEventListener('userLogin', handleUserLogin);
  }, [resolveFromSources]);

  return resolvedUserId ?? user?.userId ?? readStoredUserId() ?? null;
};

export default useResolvedUserId;
